// /admin — Admin panel with a sidebar layout.
//
// Sidebar (as specified):
//   Dashboard
//   MANAGEMENT  Users · Management · Support Tickets
//   CONTENT     ColorSep
//   SYSTEM      Login Activity · Languages · Settings · Logout
//
// Sections marked "skeleton" are intentionally place-holders (clearly
// labeled); Dashboard/Users/Management/Login Activity are live.

import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth, useAuthFetch } from '../auth/AuthProvider';
import { useLicense } from '../license/LicenseProvider';

/* ---------------- layout ---------------- */

type Section =
  | 'dashboard' | 'users' | 'management' | 'tickets'
  | 'colorsep' | 'logins' | 'languages' | 'settings' | 'logout';

const GROUPS: Array<{ title: string; items: Array<{ id: Section; label: string } | 'sep'> }> = [
  { title: '', items: [{ id: 'dashboard', label: 'Dashboard' }] },
  {
    title: 'Management',
    items: [
      { id: 'users', label: 'Users' },
      { id: 'management', label: 'Management' },
      { id: 'tickets', label: 'Support Tickets' },
    ],
  },
  { title: 'Content', items: [{ id: 'colorsep', label: 'ColorSep' }] },
  {
    title: 'System',
    items: [
      { id: 'logins', label: 'Login Activity' },
      { id: 'languages', label: 'Languages' },
      { id: 'settings', label: 'Settings' },
      { id: 'logout', label: 'Logout' },
    ],
  },
];

export function AdminPage() {
  const [section, setSection] = useState<Section>('dashboard');
  const nav = useNavigate();
  const { logout } = useAuth();

  const select = (id: string) => {
    if (id === 'logout') {
      void logout();
      nav('/', { replace: true });
      return;
    }
    setSection(id as Section);
  };

  return (
    <div data-screen className="admin-shell">
      <aside className="sidebar" aria-label="Admin navigation">
        <div className="sidebar-brand">
          <span className="logo-dot" aria-hidden="true" /> ColorSep · Admin
        </div>
        {GROUPS.map((g) => (
          <nav className="sidebar-group" key={g.title || 'root'} aria-label={g.title || 'Main'}>
            {g.title ? <div className="sidebar-title">{g.title}</div> : null}
            {g.items.map((it) =>
              typeof it === 'string' ? null : (
                <button
                  key={it.id}
                  className={`sidebar-item ${section === it.id ? 'active' : ''}`}
                  onClick={() => select(it.id)}
                  aria-current={section === it.id ? 'page' : undefined}
                >
                  {it.label}
                </button>
              ),
            )}
          </nav>
        ))}
      </aside>
      <main className="admin-main">
        {section === 'dashboard' && <Dashboard />}
        {section === 'users' && <Users />}
        {section === 'management' && <Management />}
        {section === 'tickets' && <SkeletonPage title="Support Tickets" note="Ticket kayıtları bu aşamada tutulmuyor." />}
        {section === 'colorsep' && <ColorSepContent />}
        {section === 'logins' && <LoginActivity />}
        {section === 'languages' && <SkeletonPage title="Languages" note="Arayüz dilleri (en/tr) sonraki aşamada buradan yönetilecek." />}
        {section === 'settings' && <Settings />}
      </main>
    </div>
  );
}

/* ---------------- pages ---------------- */

type DashboardData = {
  accounts: { users: number; admins: number; disabled: number };
  subscriptions: { active: number; trialing: number; expired: number; devices: number };
  backend: 'postgres' | 'file';
  logins: LoginRow[];
};

type LoginRow = {
  username: string | null;
  ip: string | null;
  success: boolean;
  ua: string | null;
  created_at: string;
};

type UserRow = {
  username: string;
  display_name: string | null;
  role: 'admin' | 'user';
  disabled: boolean;
  must_change_password: boolean;
  license: {
    status: string;
    expires_at: number | null;
    days_left: number | null;
    devices: Array<{ device_id: string; label: string | null; last_seen_at: string }>;
  } | null;
};

function Dashboard() {
  const api = useAuthFetch();
  const [data, setData] = useState<DashboardData | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let mounted = true;
    api('/admin/dashboard')
      .then((d) => mounted && setData(d as DashboardData))
      .catch((e) => mounted && setError(e.message));
    return () => { mounted = false; };
  }, [api]);

  if (error) return <div className="card">⚠️ {error}</div>;
  if (!data) return <div className="card dim">Yükleniyor…</div>;

  return (
    <>
      <h1>Dashboard</h1>
      <div className="stat-grid">
        <StatCard label="Users" value={data.accounts.users} hint={`${data.accounts.disabled} disabled`} />
        <StatCard label="Admins" value={data.accounts.admins} />
        <StatCard label="Active subscriptions" value={data.subscriptions.active} />
        <StatCard label="Trial in progress" value={data.subscriptions.trialing} />
        <StatCard label="Devices" value={data.subscriptions.devices} />
      </div>
      <section className="card" aria-label="Recent logins">
        <h2>Latest logins</h2>
        <RecentLogins rows={data.logins.slice(0, 5)} />
      </section>
      <p className="dim">Hesap deposu: {data.backend === 'postgres' ? 'Postgres (Supabase)' : 'yerel geliştirme dosyası'}</p>
    </>
  );
}

function StatCard({ label, value, hint }: { label: string; value: number; hint?: string }) {
  return (
    <div className="card stat-card" aria-label={label}>
      <div className="stat-value">{value}</div>
      <div className="stat-label">{label}</div>
      {hint ? <div className="dim">{hint}</div> : null}
    </div>
  );
}

function RecentLogins({ rows }: { rows: LoginRow[] }) {
  if (rows.length === 0) return <p className="dim">Henüz giriş yok.</p>;
  return (
    <table className="device-table">
      <thead><tr><th>User</th><th>When</th><th>Result</th><th>IP</th></tr></thead>
      <tbody>
        {rows.map((r, i) => (
          <tr key={i}>
            <td>{r.username ?? '—'}</td>
            <td>{new Date(r.created_at).toLocaleString()}</td>
            <td>{r.success ? <span className="ok-text">✅ ok</span> : <span className="err-msg">✗ failed</span>}</td>
            <td className="dim">{r.ip ?? '—'}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function Users() {
  const api = useAuthFetch();
  const [rows, setRows] = useState<UserRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [form, setForm] = useState({ username: '', password: '', role: 'user', display: '' });

  const load = () => api('/admin/users')
    .then((d) => setRows((d.users as UserRow[]) ?? []))
    .catch((e) => setError(e.message));
  useEffect(() => { void load(); /* eslint-disable-next-line */ }, []);

  const create = async () => {
    try {
      await api('/admin/users', 'POST', {
        username: form.username, password: form.password, role: form.role, display_name: form.display,
      });
      setForm({ username: '', password: '', role: 'user', display: '' });
      await load();
    } catch (e) { setError(e instanceof Error ? e.message : String(e)); }
  };

  const toggleDisable = async (u: UserRow) => {
    try {
      await api('/admin/users/disable', 'POST', { username: u.username, disabled: !u.disabled });
      await load();
    } catch (e) { setError(e instanceof Error ? e.message : String(e)); }
  };

  const resetPw = async (u: UserRow) => {
    const pw = prompt(`"${u.username}" için yeni şifre (min 8):`);
    if (!pw) return;
    try {
      await api('/admin/users/reset-password', 'POST', { username: u.username, password: pw });
      await load();
    } catch (e) { setError(e instanceof Error ? e.message : String(e)); }
  };

  // License actions on the Accounts table (username-based app-wall backend).
  const extendTrial = async (u: UserRow) => {
    const d = prompt(`"${u.username}" trial kaç gün uzatılsın?`, '30');
    if (!d) return;
    try {
      await api('/admin/license/extend', 'POST', { username: u.username, days: Number(d) });
      await load();
    } catch (e) { setError(e instanceof Error ? e.message : String(e)); }
  };

  const setLicenseStatus = async (u: UserRow, status: 'active' | 'trialing' | 'expired') => {
    const word = status === 'active' ? 'SÜRESİZ aktif' : status === 'expired' ? 'expires (kapalı)' : 'trial';
    if (!window.confirm(`"${u.username}" lisansını ${word} yapmak istiyor musunuz?`)) return;
    try {
      await api('/admin/license/set-status', 'POST', { username: u.username, status });
      await load();
    } catch (e) { setError(e instanceof Error ? e.message : String(e)); }
  };

  const revokeDevice = async (u: UserRow, deviceId: string) => {
    if (!window.confirm(`"${u.username}" kullanıcısının ${deviceId.slice(0, 12)}… cihazı geri alınsın mı?`)) return;
    try {
      await api('/admin/license/revoke-device', 'POST', { username: u.username, device_id: deviceId });
      await load();
    } catch (e) { setError(e instanceof Error ? e.message : String(e)); }
  };

  const licenseBadge = (lic: UserRow['license']) => {
    if (!lic) return <span className="dim">—</span>;
    const cls = lic.status === 'active' ? 'success' : lic.status === 'trialing' ? 'trialing' : 'expired';
    const days = lic.days_left === null ? '∞' : `${lic.days_left} g`;
    return <span className={`badge ${cls}`} title={`${lic.expires_at ? new Date(lic.expires_at * 1000).toLocaleDateString() : 'süresiz'} · ${lic.devices.length} cihaz`}>{lic.status} {days}</span>;
  };

  return (
    <>
      <h1>Users</h1>
      {error && <p className="err-msg" role="alert">{error}</p>}
      <section className="card">
        <h2>Hesap oluştur / güncelle</h2>
        <div className="row-gap" style={{ alignItems: 'flex-end' }}>
          <LabeledInput label="Kullanıcı adı" value={form.username} on={(v) => setForm({ ...form, username: v.toLowerCase() })} />
          <LabeledInput label="Şifre (min 8)" type="password" value={form.password} on={(v) => setForm({ ...form, password: v })} />
          <label className="field-label">
            Rol&nbsp;
            <select value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })}>
              <option value="user">user</option>
              <option value="admin">admin</option>
            </select>
          </label>
          <LabeledInput label="Görünen ad" value={form.display} on={(v) => setForm({ ...form, display: v })} />
          <button className="btn primary" onClick={() => void create()} disabled={!form.username || form.password.length < 8}>
            Kaydet
          </button>
        </div>
        <p className="dim">Aynı kullanıcı adı mevcutsa şifre/rol güncellenir. Bu bilgileri kullanıcıyla paylaşın.</p>
      </section>

      <section className="card">
        <h2>Accounts</h2>
        {rows && rows.length === 0 && <p className="dim">Hesap yok.</p>}
        {rows && rows.length > 0 && (
          <table className="device-table">
            <thead><tr><th>User</th><th>Role</th><th>Status</th><th>License</th><th /></tr></thead>
            <tbody>
              {rows.map((u) => (
                <tr key={u.username} className={u.disabled ? 'revoked' : ''}>
                  <td>{u.username}<span className="dim"> {u.display_name ?? ''}</span></td>
                  <td><span className="badge">{u.role}</span></td>
                  <td>{u.disabled ? <span className="err-msg">disabled</span> : <span className="ok-text">active</span>}</td>
                  <td>
                    {licenseBadge(u.license)}
                    {u.license && u.license.devices.length > 0 && (
                      <div className="dim" style={{ fontSize: 11, marginTop: 2 }}>
                        {u.license.devices.slice(0, 3).map((d) => (
                          <button
                            key={d.device_id}
                            className="link-btn dim"
                            style={{ marginRight: 6, fontSize: 11 }}
                            onClick={() => void revokeDevice(u, d.device_id)}
                            title={d.label ?? d.device_id}
                          >
                            cihaz ×{d.device_id.slice(0, 6)}…
                          </button>
                        ))}
                        {u.license.devices.length > 3 && <span>+{u.license.devices.length - 3}</span>}
                      </div>
                    )}
                  </td>
                  <td style={{ textAlign: 'right' }}>
                    <button className="btn" onClick={() => void extendTrial(u)}>Uzat +30g</button>
                    <button className="btn" style={{ marginLeft: 8 }} onClick={() => void setLicenseStatus(u, 'active')}>Aktif yap</button>
                    <button className="btn" style={{ marginLeft: 8 }} onClick={() => void setLicenseStatus(u, 'expired')}>Süre bitir</button>
                    <button className="btn" onClick={() => void resetPw(u)}>Şifre sıfırla</button>
                    <button className="btn danger-btn" style={{ marginLeft: 8 }} onClick={() => void toggleDisable(u)}>
                      {u.disabled ? 'Aktifleştir' : 'Devre dışı bırak'}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>
    </>
  );
}

function LabeledInput({ label, value, on, type = 'text' }: { label: string; value: string; on: (v: string) => void; type?: string }) {
  return (
    <label className="field-label">
      {label}
      <input type={type} value={value} onChange={(e) => on(e.target.value)} />
    </label>
  );
}

/** Subscription management — moves the old lookup card here (Supabase data). */
function Management() {
  return (
    <>
      <h1>Management</h1>
      <div className="card">
        <h2>Abonelik yönetimi</h2>
        <p className="dim">
          Üyelik/cihaz işlemleri (extend trial · expire · reactivate · revoke device) için
          kullanıcılar Supabase Auth'a kayıtlı e-postalarıyla aratılır. Yerel geliştirme
          dosya deposunda bu bölüm boştur.
        </p>
        <SubLookup />
      </div>
    </>
  );
}

type SearchResult = {
  profile: { user_id: string; email: string };
  subscription: { status: string; plan: string | null; trial_ends_at: string | null; current_period_end: string | null } | null;
  devices: Array<{ device_id: string; label: string | null; last_seen_at: string; revoked_at: string | null }>;
};

function SubLookup() {
  const api = useAuthFetch();
  const [email, setEmail] = useState('');
  const [result, setResult] = useState<SearchResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  const search = async () => {
    setError(null);
    setResult(null);
    try {
      const r = (await api(`/admin/search?email=${encodeURIComponent(email)}`)) as SearchResult;
      setResult(r);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  };

  const act = async (path: string, body: unknown, msg: string) => {
    try {
      await api(path, 'POST', body);
      alert(msg);
      await search();
    } catch (e) { setError(e instanceof Error ? e.message : String(e)); }
  };

  return (
    <div>
      <div className="row-gap">
        <input type="email" value={email} placeholder="user e-postası" onChange={(e) => setEmail(e.target.value)} />
        <button className="btn primary" disabled={!email} onClick={() => void search()}>Search</button>
      </div>
      {error && <p className="err-msg" role="alert">{error}</p>}
      {result && (
        <div style={{ marginTop: 16 }}>
          <p><span className={`badge ${result.subscription?.status ?? 'expired'}`}>{result.subscription?.status ?? 'no subscription'}</span></p>
          <div className="row-gap">
            <button className="btn" onClick={() => void act('/admin/trial-extend', { email, days: 30 }, 'Trial +30 gün')}>Extend trial +30d</button>
            <button className="btn danger-btn" onClick={() => { if (confirm('Abonelik expired yapılsın mı?')) void act('/admin/expire', { email }, 'Expired'); }}>Expire</button>
            <button className="btn" onClick={() => void act('/admin/reactivate', { email }, 'Reactivated')}>Reactivate</button>
          </div>
          <h2 style={{ marginTop: 16 }}>Devices</h2>
          {result.devices.length === 0 && <p className="dim">Cihaz yok.</p>}
          {result.devices.map((d) => (
            <div key={d.device_id} className="row-gap" style={{ padding: '4px 0' }}>
              <span className="dim">{d.device_id.slice(0, 10)}… {d.label ?? ''}</span>
              <span className="dim">{new Date(d.last_seen_at).toLocaleString()}</span>
              {!d.revoked_at && (
                <button className="btn danger-btn" onClick={() => { if (confirm('Cihaz iptal edilsin mi?')) void act('/admin/revoke-device', { email, device_id: d.device_id }, 'Cihaz iptal edildi'); }}>
                  Revoke
                </button>
              )}
              {d.revoked_at && <span className="dim">revoked</span>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function LoginActivity() {
  const api = useAuthFetch();
  const [rows, setRows] = useState<LoginRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let mounted = true;
    api('/admin/logins?limit=100')
      .then((d) => mounted && setRows((d.logins as LoginRow[]) ?? []))
      .catch((e) => mounted && setError(e.message));
    return () => { mounted = false; };
  }, [api]);

  return (
    <>
      <h1>Login Activity</h1>
      {error && <p className="err-msg" role="alert">{error}</p>}
      <section className="card">
        {rows ? <RecentLogins rows={rows} /> : <p className="dim">Yükleniyor…</p>}
      </section>
    </>
  );
}

function ColorSepContent() {
  return (
    <>
      <h1>ColorSep</h1>
      <div className="card">
        <h2>Uygulama</h2>
        <p>ColorSep editörüne gitmek için bağlantı veya kısayol yönetimi.</p>
        <a className="btn primary" href="/app">Editörü aç</a>
      </div>
    </>
  );
}

function Settings() {
  return (
    <>
      <h1>Settings</h1>
      <div className="card">
        <h2>Genel ayarlar</h2>
        <p className="dim">
          Bu aşamada ayarlar ortam değişkenleri üzerinden yönetilir
          (TRIAL_DAYS, MAX_DEVICES, TOKEN_TTL_HOURS vs. — apps/server/.env).
          Bu sayfa sonraki aşamada online ayar editörüne dönüşecek.
        </p>
      </div>
    </>
  );
}

function SkeletonPage({ title, note }: { title: string; note: string }) {
  return (
    <>
      <h1>{title}</h1>
      <div className="card">
        <p className="dim">{note}</p>
        <p className="dim">📌 Yer tutucu sayfa — içerik sonraki aşamada eklenecek.</p>
      </div>
    </>
  );
}

/** Used by the account menu → keeps the license status visible in admin too. */
export function useAdminLicenseInfo(): string {
  const { claims } = useLicense();
  if (!claims) return 'lisans yok';
  return `${claims.plan} · ${claims.status}`;
}
